import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListMusic, Wind, Music, Mic2, Play } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';
import WorkflowBuilder from './components/WorkflowBuilder';
import TimelineVisualizer from './components/TimelineVisualizer';
import SessionSummary from './components/SessionSummary';
import SavedSessionsList from './components/SavedSessionsList';
import ProgressTrackingPreview from './components/ProgressTrackingPreview';

/**
 * Custom Practice Session Builder
 * Combines Breath Coach, Pitch Coach, and Audition Song tools into structured warm-up workflows
 */
export default function CustomPracticeSessionBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State management
  const [sessionName, setSessionName] = useState('');
  const [duration, setDuration] = useState(30); // 15-60 minutes
  const [difficulty, setDifficulty] = useState('intermediate'); // beginner, intermediate, advanced
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customWorkflow, setCustomWorkflow] = useState([]);
  const [savedSessions, setSavedSessions] = useState([]);

  // Workflow configuration
  const availableTools = [
    {
      id: 'breath-coach',
      name: 'Breath Coach',
      icon: Wind,
      color: 'from-cyan-400 to-blue-500',
      presets: [
        { name: 'Box Breathing (4-4-4-4)', duration: 4, config: { inhale: 4, hold_in: 4, exhale: 4, hold_out: 4 } },
        { name: 'Performance Calm (4-7-8-0)', duration: 5, config: { inhale: 4, hold_in: 7, exhale: 8, hold_out: 0 } },
        { name: 'Quick Reset (3-3-3-3)', duration: 3, config: { inhale: 3, hold_in: 3, exhale: 3, hold_out: 3 } }
      ]
    },
    {
      id: 'pitch-coach',
      name: 'Pitch Coach',
      icon: Music,
      color: 'from-purple-400 to-pink-500',
      vocalStyles: ['Legit', 'Mix', 'Belt'],
      practiceModes: ['Melody Matching', 'Interval Training', 'Sight Reading']
    },
    {
      id: 'audition-songs',
      name: 'Audition Song Integration',
      icon: Mic2,
      color: 'from-orange-400 to-red-500',
      recommendationType: 'ai-based'
    }
  ];

  // Preset templates
  const presetTemplates = [
    {
      id: 'pre-audition',
      name: 'Pre-Audition Warm-up',
      duration: 30,
      difficulty: 'intermediate',
      workflow: [
        { id: 1, tool: 'breath-coach', preset: 'Performance Calm (4-7-8-0)', duration: 5 },
        { id: 2, tool: 'pitch-coach', vocalStyle: 'Mix', mode: 'Interval Training', duration: 10 },
        { id: 3, tool: 'audition-songs', recommendationType: 'current-role', duration: 15 }
      ],
      estimatedXP: 150
    },
    {
      id: 'daily-practice',
      name: 'Daily Practice',
      duration: 45,
      difficulty: 'beginner',
      workflow: [
        { id: 1, tool: 'breath-coach', preset: 'Box Breathing (4-4-4-4)', duration: 5 },
        { id: 2, tool: 'pitch-coach', vocalStyle: 'Legit', mode: 'Melody Matching', duration: 15 },
        { id: 3, tool: 'pitch-coach', vocalStyle: 'Mix', mode: 'Interval Training', duration: 10 },
        { id: 4, tool: 'audition-songs', recommendationType: 'skill-building', duration: 15 }
      ],
      estimatedXP: 200
    },
    {
      id: 'performance-prep',
      name: 'Performance Prep',
      duration: 60,
      difficulty: 'advanced',
      workflow: [
        { id: 1, tool: 'breath-coach', preset: 'Performance Calm (4-7-8-0)', duration: 5 },
        { id: 2, tool: 'pitch-coach', vocalStyle: 'Belt', mode: 'Sight Reading', duration: 20 },
        { id: 3, tool: 'audition-songs', recommendationType: 'performance-ready', duration: 25 },
        { id: 4, tool: 'breath-coach', preset: 'Quick Reset (3-3-3-3)', duration: 10 }
      ],
      estimatedXP: 300
    }
  ];

  // Load saved sessions from localStorage
  useEffect(() => {
    loadSavedSessions();
  }, [user]);

  const loadSavedSessions = () => {
    try {
      const saved = localStorage?.getItem(`practice_sessions_${user?.id}`);
      if (saved) {
        setSavedSessions(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved sessions:', error);
    }
  };

  // Apply preset template
  const applyPreset = (preset) => {
    setSelectedPreset(preset?.id);
    setSessionName(preset?.name);
    setDuration(preset?.duration);
    setDifficulty(preset?.difficulty);
    setCustomWorkflow(preset?.workflow);
  };

  // Add tool to workflow
  const addToolToWorkflow = (tool) => {
    const newSegment = {
      id: Date.now(),
      tool: tool?.id,
      duration: 5,
      config: {}
    };
    setCustomWorkflow([...customWorkflow, newSegment]);
  };

  // Remove tool from workflow
  const removeToolFromWorkflow = (segmentId) => {
    setCustomWorkflow(customWorkflow?.filter(seg => seg?.id !== segmentId));
  };

  // Update workflow segment
  const updateWorkflowSegment = (segmentId, updates) => {
    setCustomWorkflow(
      customWorkflow?.map(seg =>
        seg?.id === segmentId ? { ...seg, ...updates } : seg
      )
    );
  };

  // Reorder workflow (drag-and-drop)
  const reorderWorkflow = (startIndex, endIndex) => {
    const result = Array.from(customWorkflow);
    const [removed] = result?.splice(startIndex, 1);
    result?.splice(endIndex, 0, removed);
    setCustomWorkflow(result);
  };

  // Calculate total duration
  const calculateTotalDuration = () => {
    return customWorkflow?.reduce((total, seg) => total + (seg?.duration || 0), 0);
  };

  // Calculate estimated XP
  const calculateEstimatedXP = () => {
    const baseDuration = calculateTotalDuration();
    const difficultyMultiplier = difficulty === 'beginner' ? 1 : difficulty === 'intermediate' ? 1.5 : 2;
    return Math.floor(baseDuration * 3 * difficultyMultiplier);
  };

  // Save session
  const saveSession = () => {
    if (!sessionName || customWorkflow?.length === 0) {
      alert('Please provide a session name and add at least one tool to the workflow');
      return;
    }

    const sessionData = {
      id: Date.now(),
      name: sessionName,
      duration,
      difficulty,
      workflow: customWorkflow,
      estimatedXP: calculateEstimatedXP(),
      createdAt: new Date()?.toISOString()
    };

    try {
      const existingSessions = savedSessions;
      const updatedSessions = [...existingSessions, sessionData];
      localStorage?.setItem(`practice_sessions_${user?.id}`, JSON.stringify(updatedSessions));
      setSavedSessions(updatedSessions);
      
      alert('✅ Session saved successfully!');
    } catch (error) {
      console.error('Error saving session:', error);
      alert('❌ Failed to save session');
    }
  };

  // Load saved session
  const loadSession = (session) => {
    setSessionName(session?.name);
    setDuration(session?.duration);
    setDifficulty(session?.difficulty);
    setCustomWorkflow(session?.workflow);
    setSelectedPreset(null);
  };

  // Start session
  const startSession = () => {
    if (customWorkflow?.length === 0) {
      alert('Please add at least one tool to the workflow before starting');
      return;
    }

    navigate('/active-practice-session-interface', {
      state: {
        sessionName,
        duration,
        difficulty,
        workflow: customWorkflow,
        estimatedXP: calculateEstimatedXP()
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-purple-400 to-pink-500 rounded-xl">
                <ListMusic className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  🎯 Custom Practice Session Builder
                </h1>
                <p className="text-white/80">Create personalized warm-up workflows</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/the-stage-student-dashboard')}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Session Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Session Details */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <h2 className="text-xl font-bold text-white mb-4">Session Configuration</h2>
              
              {/* Session Name */}
              <div className="mb-4">
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Session Name
                </label>
                <input
                  type="text"
                  value={sessionName}
                  onChange={(e) => setSessionName(e?.target?.value)}
                  placeholder="My Awesome Warm-up"
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Duration & Difficulty */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Target Duration (minutes)
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="60"
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e?.target?.value))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Difficulty Level
                  </label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e?.target?.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              {/* Preset Templates */}
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Quick Start Presets
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {presetTemplates?.map((preset) => (
                    <button
                      key={preset?.id}
                      onClick={() => applyPreset(preset)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedPreset === preset?.id
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white/5 border-white/20 text-white/80 hover:bg-white/10'
                      }`}
                    >
                      <div className="text-sm font-bold mb-1">{preset?.name}</div>
                      <div className="text-xs opacity-70">{preset?.duration}min</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Workflow Builder */}
            <WorkflowBuilder
              availableTools={availableTools}
              customWorkflow={customWorkflow}
              onAddTool={addToolToWorkflow}
              onRemoveTool={removeToolFromWorkflow}
              onUpdateSegment={updateWorkflowSegment}
              onReorder={reorderWorkflow}
            />

            {/* Timeline Visualization */}
            <TimelineVisualizer
              workflow={customWorkflow}
              totalDuration={calculateTotalDuration()}
              targetDuration={duration}
            />
          </div>

          {/* Right: Summary & Actions */}
          <div className="space-y-6">
            {/* Session Summary */}
            <SessionSummary
              totalDuration={calculateTotalDuration()}
              targetDuration={duration}
              estimatedXP={calculateEstimatedXP()}
              workflow={customWorkflow}
            />

            {/* Saved Sessions */}
            <SavedSessionsList
              sessions={savedSessions}
              onLoadSession={loadSession}
            />

            {/* Action Buttons */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <div className="space-y-3">
                <button
                  onClick={saveSession}
                  disabled={!sessionName || customWorkflow?.length === 0}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 disabled:from-gray-500 disabled:to-gray-600 text-white font-bold rounded-xl transition-all transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
                >
                  💾 Save Session
                </button>

                <button
                  onClick={startSession}
                  disabled={customWorkflow?.length === 0}
                  className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-500 disabled:to-gray-600 text-white font-bold rounded-xl transition-all transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  🎤 Start Practice
                </button>
              </div>
            </div>

            {/* Progress Tracking Preview */}
            <ProgressTrackingPreview
              estimatedXP={calculateEstimatedXP()}
              currentStreak={5}
            />
          </div>
        </div>
      </div>
    </div>
  );
}